import {
  Avatar,
  Button,
  HStack,
  IconButton,
  MenuButton,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
} from "@chakra-ui/react";
import {
  ACTIONS_COLUMN_ID,
  type BodyColumnDef,
  DataTable,
  Link,
} from "@components/ui-kit";
import { t } from "@lingui/macro";
import {
  ClientOrderByColumnEnum,
  ProjectsWhereColumn,
  SqlOperator,
} from "@src/__generated__/urql-graphql";
import { Icon } from "@src/components/ui-kit/Icon";
import { filtersToUrlParam } from "@src/utils/routing";
import { observer } from "mobx-react-lite";
import type { FC } from "react";
import { PopoverClientActions } from "../detail/PopoverClientActions";
import { LinkIconOnHover } from "./LinkIconOnHover";
import type { ClientListItemModel } from "./models";
import { useStore } from "./store";

type TData = ClientListItemModel &
  Record<string, unknown> & {
    refetch?: () => void;
  };

const HoverLink: FC<{ id: string; name: string; addComma?: boolean }> = ({
  id,
  name,
  addComma,
}) => {
  return (
    <LinkIconOnHover dataComponentName="view-brand-text">
      <Link
        key={id + name}
        href={{
          pathname: "/brands/detail",
          query: { id },
        }}
      >
        <Text whiteSpace="normal" wordBreak="break-all">
          {`${name}${addComma ? "," : ""}`}
        </Text>
      </Link>
    </LinkIconOnHover>
  );
};

const getColumns = (): BodyColumnDef<TData, ClientOrderByColumnEnum>[] => [
  {
    id: "client",
    meta: { label: t`Client` },
    header: () => t`Client`,
    orderBy: ClientOrderByColumnEnum.ClientName,
    cell: ({ row }) => {
      const { id, name } = row.original;

      return (
        <LinkIconOnHover dataComponentName="view-client-text">
          <Link href={{ pathname: "/clients/detail", query: { id } }}>
            <Text display="flex" flexFlow="row">
              {name}
            </Text>
          </Link>
          &nbsp;
        </LinkIconOnHover>
      );
    },
  },
  {
    id: "responsible-manager",
    meta: { label: t`Responsible manager` },
    header: () => t`Responsible manager`,
    cell: ({ row }) => {
      const { responsibleManager } = row.original;

      return (
        responsibleManager && (
          <HStack>
            <Avatar
              name={responsibleManager?.full_name}
              src={responsibleManager?.image?.urls.thumbnail ?? undefined}
            />
            <Text>{responsibleManager?.full_name}</Text>
          </HStack>
        )
      );
    },
  },
  {
    id: "contact-person",
    meta: { label: t`Contact person` },
    header: () => t`Contact person`,
    cell: ({ row }) => (
      <Text>
        {row.original.contacts
          .map(({ first_name, last_name }) => `${first_name} ${last_name}`)
          .join(", ")}
      </Text>
    ),
  },
  {
    id: "brands",
    meta: { label: t`Brands` },
    header: () => t`Brands`,
    cell: ({ row }) => {
      const { brands } = row.original;
      const hiddenBrands = brands.filter((_, index) => index > 10);
      const hiddenBrandsCount = hiddenBrands.length;

      return (
        <HStack wrap="wrap" spacing="0">
          {brands
            .filter((_, index) => index <= 10)
            .map(({ id, name }, index) => (
              <HoverLink
                id={id}
                name={name}
                addComma={index < 10}
                key={id + name}
              />
            ))}
          {brands.length > 10 && (
            <Popover trigger="hover">
              <PopoverTrigger>
                <IconButton
                  aria-label={t`Brands popover trigger`}
                  icon={<Icon name="dots-horizontal" />}
                  size="sm"
                  variant="ghost"
                />
              </PopoverTrigger>
              <PopoverContent>
                <PopoverBody overflowY="auto" maxH="300px">
                  <HStack wrap="wrap" spacing="0">
                    {hiddenBrands.map(({ id, name }, index) => (
                      <HoverLink
                        id={id}
                        name={name}
                        addComma={index < hiddenBrandsCount - 1}
                        key={id + name}
                      />
                    ))}
                  </HStack>
                </PopoverBody>
              </PopoverContent>
            </Popover>
          )}
        </HStack>
      );
    },
  },
  {
    id: "workspace-company",
    meta: { label: t`Legal entity` },
    header: () => t`Legal entity`,
    cell: ({ row }) => <Text>{row.original.workspaceCompany.name}</Text>,
  },
  {
    id: "projects",
    meta: { label: t`Active projects` },
    header: () => t`Active projects`,
    cell: ({ row }) => {
      const { projectCount, brands } = row.original;

      return (
        <Link
          href={{
            pathname: "/projects",
            query: filtersToUrlParam([
              {
                column: ProjectsWhereColumn.BrandId,
                value: brands.map(({ id }) => id),
                operator: SqlOperator.In,
              },
            ]),
          }}
        >
          {projectCount}
        </Link>
      );
    },
  },
  {
    id: ACTIONS_COLUMN_ID,
    meta: { label: t`Actions` },
    header: () => t`Actions`,
    cell: ({ row }) => {
      const client = row.original;

      return (
        <PopoverClientActions
          client={{
            ...client,
            image: {
              urls: { thumbnail: client.image },
            },
          }}
          onAfterUpdate={() => {
            client.refetch?.();
          }}
          onAfterDelete={() => {
            client.refetch?.();
          }}
          trigger={
            <MenuButton as={Button} colorScheme="ghost">
              <Icon name="dots-horizontal" color="grey.700" />
            </MenuButton>
          }
        />
      );
    },
  },
];

export const TableClients = observer(function TableClients() {
  const store = useStore();

  return (
    <DataTable.Body
      tableKey="clients"
      data={
        store.clients.map((client) => ({
          ...client,
          refetch: store.refetch,
        })) as TData[]
      }
      columns={getColumns()}
      orderedBy={store.orderBy.clauses?.[0]}
      onOrderByChange={(orderBy) => {
        if (!orderBy.order) {
          store.orderBy.setClauses([]);
          return;
        }
        store.orderBy.setClauses([
          {
            column: orderBy.column,
            order: orderBy.order,
          },
        ]);
        store.refetch?.();
      }}
    />
  );
});
